/**
 * Canonical + hreflang links for the four-language site.
 *
 * Installed centrally in router.afterEach, so every page is covered without editing
 * each view. Pages whose slug is translated (product, category) call setAlternates()
 * again with explicit slugs once their data has loaded.
 */

export const SITE_URL = "https://happyplants.me";
export const LANGS = ["en", "ro", "ru", "de"];
export const X_DEFAULT_LANG = "en";

const MANAGED_ATTR = "data-seo-alternates";

/** Removes the canonical/alternate links we previously added. */
function clearManaged() {
  document
    .querySelectorAll(`link[${MANAGED_ATTR}]`)
    .forEach((el) => el.remove());
}

function appendLink(rel, href, hreflang) {
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  if (hreflang) link.setAttribute("hreflang", hreflang);
  link.setAttribute(MANAGED_ATTR, "");
  document.head.appendChild(link);
}

/**
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalized} route
 * @param {object}  [options]
 * @param {string}  [options.slugParam]      slug parameter name (product_id, collection_id, ...)
 * @param {object}  [options.slugs]          { en: '...', ro: '...', ru: '...', de: '...' }
 * @param {boolean} [options.noAlternates]   page exists in a single language only
 * @param {string}  [options.canonicalLang]  language the canonical points at when it
 *                                           differs from the URL language (blog posts)
 */
export function setAlternates(router, route, options = {}) {
  if (typeof document === "undefined") return;
  clearManaged();

  // No language in the route — an admin or service page, it needs no canonical.
  const currentLang = route.params?.lang;
  if (!currentLang || !LANGS.includes(currentLang)) return;

  const { slugParam, slugs, noAlternates, canonicalLang } = options;

  const hrefFor = (lang) => {
    const params = { ...route.params, lang };
    if (slugParam && slugs) {
      if (!slugs[lang]) return null; // no translation — leave it out of hreflang
      params[slugParam] = slugs[lang];
    }
    try {
      // The query string is dropped on purpose: a canonical has to fold utm tags and
      // filter combinations into one address, otherwise it does not do its job.
      return SITE_URL + router.resolve({ name: route.name, params }).href;
    } catch {
      return null;
    }
  };

  // The page's actual address, used when the slug has no translation.
  // A page without a self-canonical drops out of the hreflang cluster entirely.
  const selfHref = SITE_URL + route.path;

  const canonicalFor =
    canonicalLang && LANGS.includes(canonicalLang) ? canonicalLang : currentLang;
  appendLink("canonical", hrefFor(canonicalFor) || selfHref);

  if (noAlternates) return;

  const hrefs = {};
  LANGS.forEach((lang) => {
    const href = hrefFor(lang);
    if (href) hrefs[lang] = href;
  });
  // Always reference ourselves — the hreflang spec requires it.
  if (!hrefs[currentLang]) hrefs[currentLang] = selfHref;

  // hreflang only makes sense once there is more than one version
  if (Object.keys(hrefs).length < 2) return;

  Object.entries(hrefs).forEach(([lang, href]) => appendLink("alternate", href, lang));
  if (hrefs[X_DEFAULT_LANG]) {
    appendLink("alternate", hrefs[X_DEFAULT_LANG], "x-default");
  }
}

/** Applies the default canonical/hreflang on every navigation. */
export function installAlternates(router) {
  router.afterEach((to) => {
    // Blog posts are stored as a separate record per language with no link between
    // the language versions, so they get a canonical only, no alternates.
    setAlternates(router, to, { noAlternates: to.name === "blog-article" });
  });
}
